import { UsePopperState } from 'react-overlays/usePopper';
import React, { CSSProperties, forwardRef } from 'react';
import { Button } from '../../components/Button/Button';
import styled, { css } from 'styled-components';
import { PX_TO_REM, PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { IMediaSizeProps, MEDIA_SIZES, useMediaSizeCss } from '../../fundamentals/sizes/mediaSize';
import { IActionCssProps, useActionCss } from '../../fundamentals/actions';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { IBorderRadiusProps, usePanelBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { Heading6 } from '../../components/Headings/Headings';
import { IGutterAsMarginProps, IGutterProps } from '../../fundamentals/spaces/space';
import { MATERIAL_ICONS } from '../../components/Icon/icons';
import { IDisplayType } from '../../fundamentals/typography/typography';
import { defaultTourLocale, IStep, TourModalSize } from './tourModal.common';
import { CloseButton, ICloseButtonProps } from '../../components/CloseButton/CloseButton';
import { TooltipArrow } from '../../components/TooltipArrow/TooltipArrow';
import { BasePlacement, BOTTOM, IBasePlacementProps, LEFT, RIGHT, TOP, getBasePlacement } from '../../fundamentals/placements/placements';
import { BUTTON_MODES, BUTTON_VARIANTS } from '../../components/Button/buttonVariants';
import { BASIC_COLOURS } from '../../fundamentals/colours/variants';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv, BaseImg } from '../../common/BaseElements';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';
import { P } from '../../components/Texts/Texts';
import { ResponsiveGapContainer } from '../../fundamentals/spaces/responsive/SpaceContainerResponsive';

const STEP_VARIANT = BASIC_COLOURS.WHITE;

export const tourModalColours = ({ text, eoraCoreColours }: IColoursCollection) => ({
  tourModalColours: {
    // Note: Keep this shape in case variants are introduced to TourModal
    [STEP_VARIANT]: {
      arrow: {
        background: eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND
      },
      body: {
        background: eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND,
        text: text.default
      }
    }
  }
});

const popperSizes: Record<TourModalSize, PxToRem> = {
  [SIZES.X_SMALL]: PX_TO_REM[352],
  [SIZES.SMALL]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_SMALL],
  [SIZES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_MEDIUM],
  [SIZES.LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.MODAL_WIDTHS.SIZE_MODAL_WIDTH_LARGE]
};

interface IStepPopup extends Partial<IStep>, Pick<UsePopperState, 'styles'> {
  onNextClick?: React.MouseEventHandler;
  onBackClick?: React.MouseEventHandler;
  onLastClick?: React.MouseEventHandler;
  stepsCount: number;
  position?: number;
  onClose?: React.MouseEventHandler;
}

const useModalMaxWidthCss = css`
  max-width: ${({ theme }) => `calc(100% - ${theme.spaces[16]} - ${theme.spaces[16]})`};
`;

const PopupContainer = styled(BaseDiv)<Pick<IStepPopup, 'asModal'>>`
  ${useModalMaxWidthCss};
`;

const ARROW_WIDTH = PX_TO_REM[36];
const ARROW_HEIGHT = PX_TO_REM[18];

const marginMap: Partial<Record<BasePlacement, string>> = {
  [TOP]: `0 0 ${ARROW_HEIGHT} 0`,
  [RIGHT]: `0 0 0 ${ARROW_HEIGHT}`,
  [BOTTOM]: `${ARROW_HEIGHT} 0 0 0`,
  [LEFT]: `0 ${ARROW_HEIGHT} 0 0 `
};

const positionMap: Partial<Record<BasePlacement, ReturnType<typeof css>>> = {
  [TOP]: css`
    bottom: calc(-${ARROW_HEIGHT} / 2);
  `,
  [RIGHT]: css`
    left: 0;
  `,
  [BOTTOM]: css`
    top: calc(-${ARROW_HEIGHT} / 2);
  `,
  [LEFT]: css`
    right: 0;
  `
};

const PopperArrow = styled(BaseDiv)<IBasePlacementProps & IBorderRadiusProps>`
  width: calc(${ARROW_WIDTH} / 2);
  height: ${ARROW_WIDTH};

  ${({ placement }) => positionMap[placement!]}
`;

const PopperContent = styled(ResponsiveGapContainer)<IGutterProps & Pick<IStepPopup, 'size' | 'asModal'>>`
  width: ${({ size }) => popperSizes[size!]};
  max-width: 100%;
  color: ${({ theme }) => theme.tourModalColours[STEP_VARIANT].body.text};
`;

const PopperImage = styled(BaseImg)<IMediaSizeProps & IActionCssProps>`
  ${useMediaSizeCss};

  width: 100%;
  object-fit: cover;

  ${useActionCss};
`;

const PopperPager = styled(P)``;
const PopperFooter = styled(ResponsiveGapContainer)`
  align-items: center;
`;

const PopperBody = styled(P)<Pick<IStepPopup, 'allowBodyStyleOverride'> & IDisplayType>``;

const PopperButtons = styled(ResponsiveGapContainer)`
  margin-left: auto;
`;

const PopperWrapper = styled(BaseDiv)<Pick<IStepPopup, 'size' | 'asModal'> & IBasePlacementProps & IBorderRadiusProps>`
  background: ${({ theme }) => theme.tourModalColours[STEP_VARIANT].body.background};
  ${usePanelBorderRadiusCss()};
  overflow: hidden;
  ${({ placement, asModal }) =>
    marginMap[placement!] &&
    !asModal &&
    css`
      margin: ${marginMap[placement!]};
    `}
`;

const StepCloseButton = styled(CloseButton)<IBasePlacementProps & IGutterAsMarginProps & { absolute?: boolean }>`
  ${({ absolute, theme, placement }) =>
    absolute
      ? css`
          position: absolute;
          right: ${placement === 'left' ? `calc(${ARROW_HEIGHT} + ${theme.eoraSpaces.xlarge})` : theme.eoraSpaces.xlarge};
          margin-top: ${theme.eoraSpaces.xlarge};
        `
      : css`
          margin-left: auto;
        `}
`;

export const StepPopup = forwardRef<HTMLDivElement, IStepPopup>(
  (
    {
      body,
      hidePager,
      stepsCount,
      onBackClick,
      onNextClick,
      image,
      mediaSize = MEDIA_SIZES.UWIDE,
      imageAlt,
      onImageClick,
      onLastClick,
      position = 0,
      size = SIZES.SMALL,
      locale = {},
      styles,
      title,
      placement = 'bottom',
      asModal,
      closable = true,
      onClose,
      allowBodyStyleOverride = true,
      ...rest
    },
    ref
  ) => {
    const { generateTestId } = useTestId(rest, 'StepPopup');
    const { back, next, pagerSeparator, last } = { ...defaultTourLocale, ...locale };

    const basePlacement = getBasePlacement(placement);

    const closeButtonArgs: ICloseButtonProps & IBasePlacementProps = {
      placement: basePlacement,
      minimal: !!!image,
      variant: image ? 'black50NoSwitch' : 'black50',
      onClick: onClose,
      size: SIZES.X_SMALL
    };

    return (
      <PopupContainer style={asModal ? undefined : (styles.popper as CSSProperties)} ref={ref} {...generateTestId()}>
        {!asModal && (
          <PopperArrow style={styles.arrow as CSSProperties} placement={basePlacement} data-popper-arrow {...generateTestId(`arrow`)}>
            {/*TODO: use common variants. probably as const instead of enum*/}
            <TooltipArrow large placement={basePlacement} isTourModal />
          </PopperArrow>
        )}
        <PopperWrapper placement={basePlacement} {...rest}>
          {image && <PopperImage onClick={onImageClick} alt={imageAlt} src={image} mediaSize={mediaSize} {...generateTestId(`image`)} />}
          {image && closable && <StepCloseButton {...closeButtonArgs} absolute />}
          <PopperContent size={size} gutter={'medium'} gap={'medium'} column {...generateTestId(`content`)}>
            {(title || (!image && closable)) && (
              <ResponsiveGapContainer>
                {title && (
                  <Heading6 headingType='SEMI_BOLD' gutterBottom={false}>
                    {title}
                  </Heading6>
                )}
                {!image && closable && <StepCloseButton {...closeButtonArgs} />}
              </ResponsiveGapContainer>
            )}
            <PopperBody
              gutterBottom={false}
              textEoraSize='SMALL'
              textStyle='REGULAR'
              allowBodyStyleOverride={allowBodyStyleOverride}
              {...generateTestId(`body`)}
            >
              {body}
            </PopperBody>
            <PopperFooter {...generateTestId(`footer`)}>
              <PopperPager gutterBottom={false} textEoraSize='DEFAULT' textStyle='MEDIUM'>
                {position + 1} {pagerSeparator} {stepsCount}
              </PopperPager>
              <PopperButtons gap={'medium'}>
                {position > 0 && (
                  <Button
                    size={SIZES.SMALL}
                    variant={BUTTON_VARIANTS.SECONDARY}
                    mode={BUTTON_MODES.MINIMAL}
                    startIcon={MATERIAL_ICONS.ArrowBackIos}
                    onClick={onBackClick}
                    {...generateTestId(`backBtn`)}
                  >
                    {back}
                  </Button>
                )}
                {position + 1 < stepsCount ? (
                  <Button
                    size={SIZES.SMALL}
                    endIcon={MATERIAL_ICONS.ArrowForwardIos}
                    onClick={onNextClick}
                    variant={BUTTON_VARIANTS.PRIMARY}
                    {...generateTestId(`nextBtn`)}
                  >
                    {next}
                  </Button>
                ) : (
                  <Button size={SIZES.SMALL} onClick={onLastClick} variant={BUTTON_VARIANTS.PRIMARY} {...generateTestId(`lastBtn`)}>
                    {last}
                  </Button>
                )}
              </PopperButtons>
            </PopperFooter>
          </PopperContent>
        </PopperWrapper>
      </PopupContainer>
    );
  }
);
